import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { tasksForDay } from '../../api/board';
import { useApp } from '../../context/AppContext';
import { usePhone } from '../../hooks/usePhone';
import { useI18n } from '../../i18n/useI18n';
import { anchorFromOffset, isSameDay, toKey, visibleDates } from '../../utils/dates';
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from '../icons/Icons';
import { DayColumn } from './DayColumn';
import styles from './board.module.css';

const SLIDE_MS = 460;
const SLIDE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function buildStrip(before, after, delta, columns) {
  const step = Math.min(Math.abs(delta), columns);
  let dates;
  if (Math.abs(delta) < columns) {
    dates =
      delta > 0
        ? [...before, ...after.slice(columns - step)]
        : [...after.slice(0, step), ...before];
  } else {
    dates = delta > 0 ? [...before, ...after] : [...after, ...before];
  }
  const shift = (step / dates.length) * 100;
  return {
    dates,
    from: delta > 0 ? 0 : -shift,
    to: delta > 0 ? -shift : 0,
  };
}

export function DayBoard() {
  const { state, actions } = useApp();
  const { dir, t } = useI18n();
  const flow = dir === 'rtl' ? -1 : 1;
  const phone = usePhone();
  const columns = phone ? 1 : state.preferences.columns;
  const startOn = phone ? 'today' : state.preferences.startOn;
  const { dayOffset } = state;
  const dates = visibleDates(anchorFromOffset(dayOffset), columns, startOn);
  const today = new Date();
  const span = columns === 1 ? t('nav.backOne') : t('nav.backMany', { n: columns });
  const forward = columns === 1 ? t('nav.forwardOne') : t('nav.forwardMany', { n: columns });

  const [slide, setSlide] = useState(null);
  const shown = useRef({ dayOffset, columns, startOn, dates });

  useLayoutEffect(() => {
    const before = shown.current;
    const next = visibleDates(anchorFromOffset(dayOffset), columns, startOn);
    shown.current = { dayOffset, columns, startOn, dates: next };
    const delta = dayOffset - before.dayOffset;
    if (
      !delta ||
      before.columns !== columns ||
      before.startOn !== startOn ||
      prefersReducedMotion()
    ) {
      setSlide(null);
      return;
    }
    setSlide({ ...buildStrip(before.dates, next, delta, columns), moving: false });
  }, [dayOffset, columns, startOn]);

  useEffect(() => {
    if (!slide) return undefined;
    if (!slide.moving) {
      const frame = window.requestAnimationFrame(() => {
        setSlide((current) => (current === slide ? { ...current, moving: true } : current));
      });
      return () => window.cancelAnimationFrame(frame);
    }
    const timer = window.setTimeout(() => setSlide(null), SLIDE_MS + 80);
    return () => window.clearTimeout(timer);
  }, [slide]);

  const strip = slide ? slide.dates : dates;
  const trackStyle = slide
    ? {
        width: `${(slide.dates.length / columns) * 100}%`,
        transform: `translate3d(${(slide.moving ? slide.to : slide.from) * flow}%, 0, 0)`,
        transition: slide.moving ? `transform ${SLIDE_MS}ms ${SLIDE_EASE}` : 'none',
      }
    : undefined;

  return (
    <div className={styles.daysWrap}>
      <div
        className={styles.days}
        data-columns={columns}
        data-sliding={slide ? 'true' : 'false'}
      >
        <div
          className={styles.track}
          style={trackStyle}
          onTransitionEnd={(event) => {
            if (event.target === event.currentTarget && event.propertyName === 'transform') {
              setSlide(null);
            }
          }}
        >
          {strip.map((date) => {
            const dateKey = toKey(date);
            return (
              <div key={dateKey} className={styles.slot}>
                <DayColumn
                  date={date}
                  dateKey={dateKey}
                  tasks={tasksForDay(state, dateKey)}
                  isToday={isSameDay(date, today)}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className={`${styles.dayNav} ${styles.dayNavLeft}`}>
        <button
          type="button"
          className={styles.dayNavBtn}
          aria-label={t('nav.backOne')}
          title={t('nav.backOne')}
          data-mirror=""
          onClick={() => actions.shiftOffset(-1)}
        >
          <IconChevronLeft />
        </button>
        {columns > 1 && (
          <button
            type="button"
            className={styles.dayNavBtn}
            aria-label={span}
            title={span}
            data-mirror=""
            onClick={() => actions.shiftOffset(-columns)}
          >
            <IconChevronsLeft />
          </button>
        )}
      </div>
      <div className={`${styles.dayNav} ${styles.dayNavRight}`}>
        <button
          type="button"
          className={styles.dayNavBtn}
          aria-label={t('nav.forwardOne')}
          title={t('nav.forwardOne')}
          data-mirror=""
          onClick={() => actions.shiftOffset(1)}
        >
          <IconChevronRight />
        </button>
        {columns > 1 && (
          <button
            type="button"
            className={styles.dayNavBtn}
            aria-label={forward}
            title={forward}
            data-mirror=""
            onClick={() => actions.shiftOffset(columns)}
          >
            <IconChevronsRight />
          </button>
        )}
      </div>
    </div>
  );
}
